<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCompanyStore } from '@/stores/company'
import { AdsIcon } from '@accor/ads-components'
import IconStarCircle from '@/components/atoms/icons/IconStarCircle.vue'
import AllAccorBusinessLogo from '@/assets/images/all-for-business/logoAllAccorBusiness.svg'
import { useRoute, useRouter } from 'vue-router'
import UserCard from '@/components/molecules/UserCard.vue'
import { trackEvent } from '@/services/analytics-service'
import { ALL_ACCOR_BUSINESS_CONTEXT } from '@/constants/gtm4-aab'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const companyStore = useCompanyStore()
const companyName = computed(() => companyStore.company?.displayName)
const companyRewardsPoints = computed(() => companyStore.balancePoints)

const displayedRewardPoints = computed(() => {
  const points = parseInt(companyRewardsPoints.value ?? '', 10)

  if (!companyRewardsPoints.value || Number.isNaN(points))
    return t('aab.generic.rewardPoints.count.empty')

  return t('aab.navigation.menu.rewardPoints.label', { n: points })
})

const switchToB2C = () => {
  trackEvent('GTM_AAB_DASHBOARD_NAV_LINK_ACCOUNT_SWITCHER', {
    pageName: route.meta.pageName ?? '',
    blocInteraction: 'switch to personal account',
    context: ALL_ACCOR_BUSINESS_CONTEXT
  })

  router.push({ name: 'myLoyaltyProgram' })
}
</script>

<template>
  <UserCard :can-switch="true" :switch-label="t('navigationBar.switchToB2C')" @switch="switchToB2C">
    <template #top>
      <div class="user-card-aab__info">
        <div class="user-card-aab__text">
          <p class="user-card-aab__greeting" data-cs-mask>{{ companyName }}</p>
          <p class="user-card-aab__status">{{ t('aab.navigation.menu.companyDashboard.label') }}</p>
        </div>
        <div class="user-card-aab__notification">
          <AdsIcon :icon="AllAccorBusinessLogo" size="lg" class="user-card-aab__logo" />
        </div>
      </div>
    </template>
    <template #bottom>
      <div class="user-card-aab__stat">
        <AdsIcon :icon="IconStarCircle" size="sm" fill="none" />
        <p>{{ displayedRewardPoints }}</p>
      </div>
    </template>
  </UserCard>
</template>

<style lang="scss">
@use '@/styles/variables' as *;

.user-card-aab {
  &__info {
    display: flex;
    flex-direction: row;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-md;
  }

  &__text {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__greeting {
    @include ads-sass.title-03;
    color: ads.$ads-primary;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;
  }

  &__notification {
    position: relative;
    margin-left: auto;
  }

  &__logo {
    background-color: ads.$ads-on-loyalty;
    border-radius: ads.$ads-border-radius-element-md;
  }

  &__stat {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-xs;
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;
  }
}
</style>
